"use client";

import Link from "next/link";
import { m } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { visiblePillars, type PillarId } from "@/lib/services/pillars";
import { MockupImage } from "@/components/services/mockup-image";

/* ─────────────────────────────────────────────────────────────
 * Leistungen auf der Startseite – die drei Säulen Design, Marketing
 * und Development aus src/lib/services/pillars.ts (dieselbe Quelle wie
 * das Mega-Menü im Header). Jede Säule zeigt oben das Mockup-Bild der
 * Säule, darunter die Leistungen als Liste. Sichtbarkeit steuert
 * Admin → Dienstleistungen.
 * ──────────────────────────────────────────────────────────── */

type Tone = { fg: string; bg: string };

const TONES: Record<PillarId, Tone> = {
  design: { fg: "var(--fi-brand-fg)", bg: "var(--fi-brand-bg)" },
  marketing: { fg: "#7C3AED", bg: "#EDE9FE" },
  development: { fg: "#2563EB", bg: "#DBEAFE" },
};

export function ServicesPreview({
  visibleServiceSlugs,
}: {
  /** Öffentlich sichtbare Dienstleistungen (Admin → Dienstleistungen). */
  visibleServiceSlugs?: string[];
} = {}) {
  const pillars = visiblePillars(visibleServiceSlugs);

  return (
    <section className="py-16 lg:py-24" style={{ backgroundColor: "var(--bg-secondary)" }}>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <m.div
          className="mx-auto max-w-2xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-sm font-semibold text-brand-500">Leistungen</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl" style={{ color: "var(--text)", letterSpacing: "-0.02em" }}>
            Unsere Dienstleistungen
          </h2>
          <p className="mt-4 text-lg leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
            Design, Marketing und Development – alle Leistungen aus einer Hand.
          </p>
        </m.div>

        <div className={`mt-14 grid gap-6 ${pillars.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"}`}>
          {pillars.map((pillar, i) => {
            const tone = TONES[pillar.id];
            const PillarIcon = pillar.icon;
            return (
              <m.div
                key={pillar.id}
                className="flex flex-col overflow-hidden rounded-2xl border"
                style={{ backgroundColor: "var(--card-bg)", borderColor: "var(--border)", boxShadow: "var(--card-shadow)" }}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
              >
                {/* Mockup-Bild der Säule (Claude Design) */}
                <div className="p-4 pb-0" style={{ backgroundColor: "var(--bg-secondary)" }}>
                  <MockupImage name={pillar.image.name} alt={pillar.image.alt} sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw" className="translate-y-px" />
                </div>

                {/* Säule */}
                <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: "var(--border)" }}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: tone.bg, color: tone.fg }}>
                    <PillarIcon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-base font-semibold" style={{ color: "var(--text)" }}>{pillar.label}</p>
                    <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>{pillar.tagline}</p>
                  </div>
                </div>

                {/* Leistungen der Säule */}
                <ul className="flex flex-1 flex-col divide-y" style={{ borderColor: "var(--border)" }}>
                  {pillar.services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <li key={service.href} style={{ borderColor: "var(--border)" }}>
                        <Link href={service.href} className="group flex gap-3 px-5 py-4 transition-colors hover:bg-brand-500/5">
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors" style={{ borderColor: "var(--border)", backgroundColor: "var(--bg)", color: "var(--text-tertiary)" }}>
                            <Icon className="h-4 w-4 transition-colors group-hover:text-brand-500" aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center justify-between gap-2">
                              <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>{service.title}</span>
                              <ArrowRight className="h-4 w-4 shrink-0 text-brand-500 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" aria-hidden="true" />
                            </span>
                            <span className="mt-1 block text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>{service.description}</span>
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex items-center gap-1.5 border-t px-5 py-3 text-xs" style={{ borderColor: "var(--border)", color: "var(--text-tertiary)" }}>
                  <Check className="h-3.5 w-3.5 text-brand-500" aria-hidden="true" />
                  Aus einer Hand mit den anderen Bereichen
                </div>
              </m.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
